/**
 * Initializes the category toggles for the Google Map layout variant 2.
 * This turns the category headings (h6) and marker lists (ol) into an
 * accessible tab interface.
 *
 * - Clicking a category heading reveals its associated list of markers.
 * - Manages `active` classes for CSS transitions.
 * - Implements WAI-ARIA roles and properties for accessibility, including keyboard navigation.
 */
export function initializeGoogleMapCategoryToggles() {
    const mapControlBlocks = document.querySelectorAll('.layout__google_map--2 .google-map__controls');

    mapControlBlocks.forEach(controlBlock => {
        const fieldset = controlBlock.querySelector('fieldset[data-control-type="markers"]');
        if (!fieldset) {
            return;
        }

        const tabs = Array.from(fieldset.querySelectorAll('h6'));
        // Ensure we only get direct `ol` siblings that correspond to `h6`s
        const panels = tabs.map(tab => tab.nextElementSibling).filter(el => el && el.tagName === 'OL');

        if (tabs.length === 0 || tabs.length !== panels.length) {
            console.warn('Google Map Category Toggle: Mismatch between tabs (h6) and panels (ol), or none found.');
            return;
        }

        // Set up ARIA roles
        fieldset.setAttribute('role', 'tablist');

        tabs.forEach((tab, index) => {
            const panel = panels[index];
            const uniqueId = `map-category-${Math.random().toString(36).substring(2, 9)}`;
            const tabId = `${uniqueId}-tab`;
            const panelId = `${uniqueId}-panel`;

            // Make h6 focusable and behave like a button
            tab.setAttribute('role', 'tab');
            tab.setAttribute('id', tabId);
            tab.setAttribute('aria-controls', panelId);
            tab.setAttribute('tabindex', '-1'); // Not focusable by default, manage focus with JS

            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('id', panelId);
            panel.setAttribute('aria-labelledby', tabId);

            if (index === 0) {
                // Activate the first tab by default
                tab.classList.add('active');
                tab.setAttribute('aria-selected', 'true');
                tab.setAttribute('tabindex', '0');
                panel.classList.add('active');
            } else {
                tab.setAttribute('aria-selected', 'false');
                panel.classList.remove('active');
            }

            // Click event listener
            tab.addEventListener('click', () => {
                activateTab(tab, tabs, panels);
            });

            // Keyboard event listener
            tab.addEventListener('keydown', (e) => {
                handleKeydown(e, index, tabs, panels);
            });
        });
    });
}

/**
 * Activates a specific tab and its panel.
 * @param {HTMLElement} tabToActivate The tab element to activate.
 * @param {Array<HTMLElement>} allTabs All tab elements in the group.
 * @param {Array<HTMLElement>} allPanels All panel elements in the group.
 */
function activateTab(tabToActivate, allTabs, allPanels) {
    // Deactivate all tabs and panels
    allTabs.forEach((tab, index) => {
        const panel = allPanels[index];
        tab.classList.remove('active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
        if (panel) {
            panel.classList.remove('active');
        }
    });

    // Activate the clicked tab and its panel
    const panelToActivate = document.getElementById(tabToActivate.getAttribute('aria-controls'));
    tabToActivate.classList.add('active');
    tabToActivate.setAttribute('aria-selected', 'true');
    tabToActivate.setAttribute('tabindex', '0');
    if (panelToActivate) {
        panelToActivate.classList.add('active');
    }
    tabToActivate.focus();
}

/**
 * Handles keyboard navigation for the tablist.
 * @param {KeyboardEvent} e The keyboard event.
 * @param {number} currentIndex The index of the currently focused tab.
 * @param {Array<HTMLElement>} tabs The array of all tab elements.
 * @param {Array<HTMLElement>} panels The array of all panel elements.
 */
function handleKeydown(e, currentIndex, tabs, panels) {
    const key = e.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'Home' && key !== 'End') {
        return;
    }
    e.preventDefault();

    let nextIndex = currentIndex;
    if (key === 'ArrowRight' || key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
    else if (key === 'ArrowLeft' || key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    else if (key === 'Home') nextIndex = 0;
    else if (key === 'End') nextIndex = tabs.length - 1;

    activateTab(tabs[nextIndex], tabs, panels);
}